import React, { useState } from 'react';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
} from 'recharts';
import { useTranslation } from 'react-i18next';
import Icon from '../../components/Icon';
import ChartTooltip from './ChartTooltip';
import { useChartPalette } from '../useThemeMode';
import {
  CUSTOMER_GROWTH, CUSTOMER_DEMOGRAPHICS, CUSTOMER_REGIONS, KEY_METRICS,
} from '../mockData';

const TABS = [
  { id: 'growth', icon: 'trending-up' },
  { id: 'demographics', icon: 'users' },
  { id: 'regions', icon: 'map-pin' },
];

const DistributionList = ({ title, items, color }) => (
  <div className="stats-distribution">
    <h4 className="stats-block-title">{title}</h4>
    <ul className="stats-distribution-list">
      {items.map((item) => (
        <li key={item.id}>
          <span className="stats-distribution-label">{item.label}</span>
          <span className="stats-progress">
            <span className="stats-progress-fill" style={{ width: `${item.value}%`, background: color }} />
          </span>
          <span className="stats-distribution-value">{item.value}%</span>
        </li>
      ))}
    </ul>
  </div>
);

const CustomerInsights = () => {
  const { t } = useTranslation();
  const palette = useChartPalette();
  const [tab, setTab] = useState('growth');

  return (
    <section className="stats-card">
      <header className="stats-card-head">
        <div>
          <h3 className="stats-card-title">{t('statistics.insights.title')}</h3>
          <p className="stats-card-subtitle">{t('statistics.insights.subtitle')}</p>
        </div>
      </header>

      <div className="stats-tabs" role="tablist">
        {TABS.map((item) => (
          <button
            key={item.id}
            type="button"
            role="tab"
            aria-selected={tab === item.id}
            className={`stats-tab${tab === item.id ? ' active' : ''}`}
            onClick={() => setTab(item.id)}
          >
            <Icon name={item.icon} size={16} />
            {t(`statistics.insights.tabs.${item.id}`)}
          </button>
        ))}
      </div>

      <div className="stats-insights-grid">
        <div className="stats-insights-main">
          {tab === 'growth' && (
            <>
              <h4 className="stats-block-title">{t('statistics.insights.growthTitle')}</h4>
              <div className="stats-chart" style={{ height: 320 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={CUSTOMER_GROWTH} margin={{ top: 10, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="4 4" stroke={palette.grid} vertical={false} />
                    <XAxis
                      dataKey="month"
                      tickLine={false}
                      axisLine={false}
                      tick={{ fill: palette.axis, fontSize: 12 }}
                      dy={8}
                    />
                    <YAxis
                      tickLine={false}
                      axisLine={false}
                      tick={{ fill: palette.axis, fontSize: 12 }}
                      width={44}
                    />
                    <Tooltip cursor={{ fill: palette.grid, opacity: 0.35 }} content={<ChartTooltip />} />
                    <Bar dataKey="newCustomers" name={t('statistics.insights.series.new')} fill={palette.series.primary} radius={[4, 4, 0, 0]} />
                    <Bar dataKey="returning" name={t('statistics.insights.series.returning')} fill={palette.series.success} radius={[4, 4, 0, 0]} />
                    <Bar dataKey="churned" name={t('statistics.insights.series.churned')} fill={palette.series.warning} radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </>
          )}

          {tab === 'demographics' && (
            <DistributionList
              title={t('statistics.insights.ageTitle')}
              items={CUSTOMER_DEMOGRAPHICS}
              color={palette.series.primary}
            />
          )}

          {tab === 'regions' && (
            <DistributionList
              title={t('statistics.insights.regionsTitle')}
              items={CUSTOMER_REGIONS}
              color={palette.series.success}
            />
          )}
        </div>

        <aside className="stats-insights-side">
          <h4 className="stats-block-title">{t('statistics.insights.keyMetrics')}</h4>
          {KEY_METRICS.map((metric) => (
            <div className="stats-metric" key={metric.id}>
              <span className="stats-metric-label">
                <Icon name={metric.icon} size={14} />
                {metric.label}
              </span>
              <span className="stats-metric-value">{metric.value}</span>
              <span className="stats-metric-delta">↑ {metric.delta}</span>
            </div>
          ))}
        </aside>
      </div>
    </section>
  );
};

export default CustomerInsights;
